import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth, API } from '../App';
import { SHOW_PRICING } from '../App';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Gift, ArrowRight, Loader2, AlertCircle, CheckCircle, UserPlus, Bell } from 'lucide-react';

const JoinBeta = () => {
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const [searchParams] = useSearchParams();
  const inviteCode = searchParams.get('invite') || '';
  
  const [validating, setValidating] = useState(true);
  const [inviteValid, setInviteValid] = useState(false);
  const [inviterName, setInviterName] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [betaFull, setBetaFull] = useState(false);
  
  // Registration form
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [registering, setRegistering] = useState(false);
  
  // Waitlist form
  const [waitlistEmail, setWaitlistEmail] = useState('');
  const [waitlistName, setWaitlistName] = useState('');
  const [waitlistLoading, setWaitlistLoading] = useState(false);
  const [waitlistPosition, setWaitlistPosition] = useState(null);

  useEffect(() => {
    if (inviteCode) {
      validateInviteCode();
    } else {
      setValidating(false);
      setErrorMessage('No invitation code provided');
    }
  }, [inviteCode]);

  const validateInviteCode = async () => {
    try {
      const response = await axios.post(`${API}/beta/validate-invite`, { code: inviteCode });
      if (response.data.valid) {
        setInviteValid(true);
        setInviterName(response.data.inviter_name || 'A Beta Tester');
      } else {
        setErrorMessage(response.data.message || 'Invalid invitation link');
        setBetaFull(response.data.beta_full || false);
      }
    } catch (error) {
      setErrorMessage('Failed to validate invitation link');
    } finally {
      setValidating(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setRegistering(true);
    try {
      const response = await axios.post(`${API}/beta/register-with-invite`, {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        invite_code: inviteCode
      });
      
      // Log user in
      login(response.data.token, response.data.user);
      toast.success('Account created! Welcome to Vision Lab Beta.');
      
      // Redirect to Beta checkout
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setRegistering(false);
    }
  };

  const handleJoinWaitlist = async () => {
    if (!waitlistEmail) {
      toast.error('Please enter your email');
      return;
    }
    
    setWaitlistLoading(true);
    try {
      const response = await axios.post(`${API}/beta/join-waitlist`, {
        email: waitlistEmail,
        name: waitlistName
      });
      setWaitlistPosition(response.data.position);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to join waitlist');
    } finally {
      setWaitlistLoading(false);
    }
  };

  // If user is already logged in
  if (user && inviteValid) {
    return (
      <div className="min-h-screen bg-[#050505] noise-overlay flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full text-center"
        >
          <div className="card-premium p-8">
            <CheckCircle className="text-[#D4AF37] mx-auto mb-4" size={48} />
            <h2 className="font-serif text-2xl text-white mb-4">You're Already Signed In</h2>
            <p className="text-[#A1A1AA] mb-6">
              You have a valid invitation from {inviterName}. 
              Continue to checkout to activate your Beta access.
            </p>
            <Button onClick={() => navigate('/dashboard')} className="btn-gold w-full">
              Continue to Beta Checkout
              <ArrowRight className="ml-2" size={16} />
            </Button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] noise-overlay">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 glass border-b border-white/5">
        <div className="max-w-7xl mx-auto px-8 py-5 flex items-center justify-between">
          <Link to="/" className="group" data-testid="logo">
            <span className="font-serif text-2xl text-white tracking-wide">Clierxter</span>
            <span className="block text-[#D4AF37] text-[10px] font-accent tracking-[0.3em] mt-0.5">VISION LAB</span>
          </Link>
        </div>
      </nav>

      <div className="max-w-lg mx-auto px-4 py-16">
        {/* Loading State */}
        {validating && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-16"
          >
            <Loader2 className="animate-spin text-[#D4AF37] mx-auto mb-4" size={48} />
            <p className="text-[#A1A1AA]">Validating your invitation...</p>
          </motion.div>
        )}

        {/* Error State */}
        {!validating && !inviteValid && !betaFull && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
            data-testid="invite-error"
          >
            <div className="card-premium p-8">
              <AlertCircle className="text-red-500 mx-auto mb-4" size={48} />
              <h2 className="font-serif text-2xl text-white mb-4">Invalid Invitation</h2>
              <p className="text-[#A1A1AA] mb-6">{errorMessage}</p>
              <div className="space-y-3">
                <Button onClick={() => navigate('/dashboard')} className="btn-gold w-full">
                  View Pricing Options
                </Button>
                <Button onClick={() => navigate('/auth')} className="btn-outline-gold w-full">
                  Sign In
                </Button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Beta Full - Waitlist */}
        {!validating && betaFull && !waitlistPosition && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
            data-testid="beta-full-waitlist"
          >
            <div className="card-premium p-8">
              <Bell className="text-[#D4AF37] mx-auto mb-4" size={48} />
              <h2 className="font-serif text-2xl text-white mb-4">Beta Program is Full</h2>
              <p className="text-[#A1A1AA] mb-6">
                All 20 spots have been taken! Join the waitlist to be notified when a spot opens up.
              </p>
              
              <div className="space-y-4 text-left">
                <div>
                  <label className="block text-[#71717A] text-sm mb-2">Name</label>
                  <Input
                    type="text"
                    value={waitlistName}
                    onChange={(e) => setWaitlistName(e.target.value)}
                    placeholder="Your name"
                    className="bg-[#0A0A0A] border-[#27272A] text-white"
                    data-testid="join-waitlist-name"
                  />
                </div>
                <div>
                  <label className="block text-[#71717A] text-sm mb-2">Email</label>
                  <Input
                    type="email"
                    value={waitlistEmail}
                    onChange={(e) => setWaitlistEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="bg-[#0A0A0A] border-[#27272A] text-white"
                    data-testid="join-waitlist-email"
                  />
                </div>
                <Button
                  onClick={handleJoinWaitlist}
                  disabled={waitlistLoading}
                  className="btn-gold w-full py-4"
                  data-testid="join-waitlist-submit"
                >
                  {waitlistLoading ? (
                    <>
                      <Loader2 className="animate-spin mr-2" size={16} />
                      Joining...
                    </>
                  ) : (
                    <>
                      <Bell className="mr-2" size={16} />
                      Join Waitlist
                    </>
                  )}
                </Button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Waitlist Confirmation */}
        {waitlistPosition && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
            data-testid="waitlist-success"
          >
            <div className="card-premium p-8">
              <CheckCircle className="text-[#D4AF37] mx-auto mb-4" size={48} />
              <h2 className="font-serif text-2xl text-white mb-4">You're on the List!</h2>
              <p className="text-[#A1A1AA] mb-4">
                Your position: <span className="text-[#D4AF37] font-bold">#{waitlistPosition}</span>
              </p>
              <p className="text-[#71717A] text-sm mb-6">
                We'll contact you as soon as a spot opens up.
              </p>
              <Button onClick={() => navigate('/')} className="btn-outline-gold w-full">
                Back to Home
              </Button>
            </div>
          </motion.div>
        )}

        {/* Valid Invite - Registration Form */}
        {!validating && inviteValid && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            data-testid="registration-form"
          >
            {/* Invitation Badge */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 px-4 py-2 mb-4">
                <Gift className="text-[#D4AF37]" size={18} />
                <span className="text-[#D4AF37] text-sm font-accent">INVITED BY {inviterName.toUpperCase()}</span>
              </div>
              <h1 className="font-serif text-3xl text-white mb-2">Welcome to Vision Lab</h1>
              <p className="text-[#A1A1AA]">Create your account to join the Beta program</p>
            </div>

            {/* Registration Form */}
            <div className="card-premium p-8">
              <form onSubmit={handleRegister} className="space-y-5">
                <div>
                  <label className="block text-[#71717A] text-sm mb-2">Full Name</label>
                  <Input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Your name"
                    required
                    className="bg-[#0A0A0A] border-[#27272A] text-white"
                    data-testid="register-name"
                  />
                </div>
                
                <div>
                  <label className="block text-[#71717A] text-sm mb-2">Email</label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="your@email.com"
                    required
                    className="bg-[#0A0A0A] border-[#27272A] text-white"
                    data-testid="register-email"
                  />
                </div>
                
                <div>
                  <label className="block text-[#71717A] text-sm mb-2">Password</label>
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="At least 6 characters"
                    required
                    className="bg-[#0A0A0A] border-[#27272A] text-white"
                    data-testid="register-password"
                  />
                </div>
                
                <div>
                  <label className="block text-[#71717A] text-sm mb-2">Confirm Password</label>
                  <Input
                    type="password"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    placeholder="Confirm your password"
                    required
                    className="bg-[#0A0A0A] border-[#27272A] text-white"
                    data-testid="register-confirm-password"
                  />
                </div>
                
                <Button
                  type="submit"
                  disabled={registering}
                  className="btn-gold w-full py-4"
                  data-testid="register-submit"
                >
                  {registering ? (
                    <>
                      <Loader2 className="animate-spin mr-2" size={16} />
                      Creating Account...
                    </>
                  ) : (
                    <>
                      <UserPlus className="mr-2" size={16} />
                      Create Account & Join Beta
                    </>
                  )}
                </Button>
              </form>
              
              <div className="mt-6 text-center">
                <p className="text-[#52525B] text-sm">
                  Already have an account?{' '}
                  <Link to="/auth" className="text-[#D4AF37] hover:underline">
                    Sign In
                  </Link>
                </p>
              </div>
            </div>
            
            {/* Beta Info */}
            <div className="mt-6 bg-[#D4AF37]/5 border border-[#D4AF37]/20 p-4 text-center">
              <p className="text-[#A1A1AA] text-sm">
                Beta Access: <span className="text-[#D4AF37]">€20</span> for 4 weeks of Pro features
              </p>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default JoinBeta;
